import React from 'react';
import { DayPickerProps } from 'react-day-picker';

import { useIsLocalityPage } from '@/hooks/useIsLocalityPage';
import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';
import { useRouter } from '@/hooks/useRouter';
import { IHeaderSearchFilterDates, IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';

import DesktopNavbarSearch from './DesktopNavbarSearch/DesktopNavbarSearch';
import MobileNavbarSearch from './MobileNavbarSearch/MobileNavbarSearch';

interface INavbarSearchProps {
  onSubmit?: () => void;
  handleRecentSearchNavigation?: (url: string) => void;
  location?: { lat: number; lng: number };
  address?: string;
  guests?: IHeaderSearchFilterGuests;
  dates?: IHeaderSearchFilterDates;
  recentSearches?: ILocationSearchAutocompleteListOption[];
  maxRecentSearches: number;
  loading?: boolean;
  isHomePage?: boolean;
  onChangeAddress?: (address?: string, addressOption?: any) => void;
  onChangeDates: (address?: IHeaderSearchFilterDates) => void;
  onChangeGuests?: (guests?: IHeaderSearchFilterGuests) => void;
  onConfirmAddress?: (address?: string) => void;
  onConfirmDates?: (dates?: IHeaderSearchFilterDates) => void;
  onConfirmGuests?: (guests: IHeaderSearchFilterGuests) => void;
  className?: string;
  onClose?: () => void;
  onOpen?: () => void;
  popularDestinations?: ILocationSearchAutocompleteListOption[];
  alertStatus?: boolean;
  isPromoSearch?: boolean;
  isGuestOccupancyAvailable?: boolean;
  isSERP?: boolean;
  scrollToSearchFilter?: () => void;
  variant?: 'minimal' | 'enhanced';
  disabledDays?: DayPickerProps['disabled'];
}

const NavbarSearch: React.FC<INavbarSearchProps> = ({
  onSubmit,
  location,
  address,
  dates,
  guests,
  recentSearches = [],
  maxRecentSearches,
  loading = false,
  onChangeAddress,
  onChangeDates,
  onChangeGuests,
  onConfirmAddress,
  onConfirmDates,
  onConfirmGuests,
  className,
  onClose,
  onOpen,
  popularDestinations,
  alertStatus,
  handleRecentSearchNavigation,
  isPromoSearch,
  isGuestOccupancyAvailable,
  isSERP,
  scrollToSearchFilter,
  variant,
  disabledDays = [],
}) => {
  const router = useRouter();
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const isRVSearchPage = router.pathname === '/rv-search';
  const { isLocalityPage } = useIsLocalityPage();
  const useModeSwitcher =
    isRentalsVDeliverySearchModeEnabled &&
    !variant &&
    (isRVSearchPage || isPromoSearch || isLocalityPage);

  return (
    <>
      <div className={`hidden md:block ${className || ''}`}>
        <DesktopNavbarSearch
          location={location}
          address={address}
          dates={dates}
          guests={guests}
          recentSearches={recentSearches}
          maxRecentSearches={maxRecentSearches}
          onSubmit={onSubmit}
          loading={loading}
          onChangeAddress={onChangeAddress}
          onChangeDates={onChangeDates}
          onChangeGuests={onChangeGuests}
          onConfirmAddress={onConfirmAddress}
          onConfirmDates={onConfirmDates}
          onConfirmGuests={onConfirmGuests}
          popularDestinations={popularDestinations}
          alertStatus={alertStatus}
          handleRecentSearchNavigation={handleRecentSearchNavigation}
          isPromoSearch={isPromoSearch}
          variant={variant}
          isGuestOccupancyAvailable={isGuestOccupancyAvailable}
          isSERP={isSERP}
          scrollToSearchFilter={scrollToSearchFilter}
          disabledDays={disabledDays}
          useModeSwitcher={useModeSwitcher}
        />
      </div>
      <div className={`w-full md:hidden grid grid-flow-col auto-cols-fr ${className || ''}`}>
        <MobileNavbarSearch
          location={location}
          address={address}
          dates={dates}
          guests={guests}
          recentSearches={recentSearches}
          maxRecentSearches={maxRecentSearches}
          onChangeAddress={onChangeAddress}
          onChangeDates={onChangeDates}
          onChangeGuests={onChangeGuests}
          onClose={onClose}
          onOpen={onOpen}
          loading={loading}
          onSubmit={onSubmit}
          popularDestinations={popularDestinations}
          handleRecentSearchNavigation={handleRecentSearchNavigation}
          variant={variant}
          isGuestOccupancyAvailable={isGuestOccupancyAvailable}
          scrollToSearchFilter={scrollToSearchFilter}
          disabledDays={disabledDays}
          isSERP={isSERP}
          useModeSwitcher={useModeSwitcher}
        />
      </div>
    </>
  );
};

export default NavbarSearch;
